import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/FormField";

export const SignaturePad = ({ value = {}, onChange, field, error }) => {
  const canvas = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value.drawn_png);
  const set = (patch) => onChange({ typed_name: "", consent_to_esign: false, ...value, ...patch });

  const pos = (e) => {
    const r = canvas.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: ((p.clientX - r.left) / r.width) * canvas.current.width, y: ((p.clientY - r.top) / r.height) * canvas.current.height };
  };
  const start = (e) => { drawing.current = true; const ctx = canvas.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvas.current.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#1A1A1A"; const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); setHasInk(true); };
  const end = () => { if (!drawing.current) return; drawing.current = false; set({ drawn_png: canvas.current.toDataURL("image/png") }); };
  const clear = () => { const c = canvas.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); set({ drawn_png: null }); };

  return (
    <div className="surface p-5" data-testid="signature-pad">
      <label className="tap justify-start gap-3 cursor-pointer">
        <input type="checkbox" className="h-5 w-5 accent-[#212B23]" checked={!!value.consent_to_esign} onChange={(e) => set({ consent_to_esign: e.target.checked })} data-testid="esign-consent" />
        <span>I agree to sign this document electronically.</span>
      </label>
      <Field label={field.label} required={field.required} help={field.help} error={error}>
        {(p) => <input {...p} className="input font-serif text-xl" autoComplete="name" value={value.typed_name || ""} onChange={(e) => set({ typed_name: e.target.value })} data-testid="esign-typed-name" />}
      </Field>
      <p className="label">Draw your signature <span className="font-normal text-ink-500">(optional)</span></p>
      <canvas ref={canvas} width={600} height={180} className="w-full touch-none border border-sage-400 bg-white" aria-label="Signature drawing area"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="esign-canvas" />
      <button type="button" className="btn-quiet mt-2" onClick={clear} disabled={!hasInk} data-testid="esign-clear">Clear Drawing</button>
    </div>
  );
};

export const DependentPicker = ({ value, onChange, field, error }) => {
  const qc = useQueryClient();
  const { data: deps = [] } = useQuery({ queryKey: ["household", "dependents"], queryFn: async () => (await api.get("/household/dependents")).data });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", date_of_birth: "", relationship: "" });
  const [err, setErr] = useState("");
  useEffect(() => { if (deps.length === 0) setAdding(true); }, [deps.length]);
  const add = async () => {
    setErr("");
    try {
      const { data } = await api.post("/household/dependents", form);
      qc.invalidateQueries({ queryKey: ["household"] });
      onChange(data.id); setAdding(false); setForm({ first_name: "", last_name: "", date_of_birth: "", relationship: "" });
    } catch (e) { setErr(apiError(e)); }
  };
  return (
    <fieldset className="mb-6" data-testid="dependent-picker">
      <legend className="label">{field.label}{field.required && <span className="text-ink-500 font-normal"> (required)</span>}</legend>
      {field.help && <p className="help mb-2">{field.help}</p>}
      <div className="flex flex-col gap-2">
        {deps.map((d) => (
          <label key={d.id} className="tap justify-start gap-3 border border-sage-400 bg-cream-100 px-4 cursor-pointer has-[:checked]:border-olive-900 has-[:checked]:ring-2 has-[:checked]:ring-olive-900/20">
            <input type="radio" name={field.key} className="h-5 w-5 accent-[#212B23]" checked={value === d.id} onChange={() => onChange(d.id)} data-testid={`dependent-option-${d.id}`} />
            <span>{d.first_name} {d.last_name} <span className="text-ink-500">· {d.relationship}</span></span>
          </label>
        ))}
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {adding ? (
        <div className="surface mt-4 p-4" data-testid="dependent-add-form">
          <p className="font-serif text-lg font-semibold">Add A Child To Your Household</p>
          {err && <p className="field-error" role="alert">{err}</p>}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[["first_name", "First name", "text"], ["last_name", "Last name", "text"], ["date_of_birth", "Date of birth", "date"], ["relationship", "Your relationship (e.g. parent, guardian)", "text"]].map(([k, label, type]) => (
              <div key={k}><label htmlFor={`dep-${k}`} className="label text-sm">{label}</label><input id={`dep-${k}`} type={type} className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`dependent-${k}`} /></div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className="btn-primary" onClick={add} disabled={!form.first_name || !form.last_name || !form.date_of_birth || !form.relationship} data-testid="dependent-save">Add Child</button>
            {deps.length > 0 && <button type="button" className="btn-quiet" onClick={() => setAdding(false)}>Cancel</button>}
          </div>
        </div>
      ) : (
        <button type="button" className="btn-secondary mt-3" onClick={() => setAdding(true)} data-testid="dependent-add">Add Another Child</button>
      )}
    </fieldset>
  );
};

export const FieldInput = ({ field, value, onChange, error }) => {
  const tid = `field-${field.key}`;
  if (field.type === "rich_text") return <div className="prose-care mb-6">{field.label && <p className="font-semibold">{field.label}</p>}<p>{field.help}</p></div>;
  if (field.type === "signature") return <SignaturePad field={field} value={value || {}} onChange={onChange} error={error} />;
  if (field.type === "dependent") return <DependentPicker field={field} value={value} onChange={onChange} error={error} />;
  if (field.type === "acknowledgment") {
    return (
      <div className="mb-5" data-testid={tid}>
        <label className={`flex gap-3 border p-4 cursor-pointer ${error ? "border-terracotta-600" : "border-sage-400"} bg-cream-100 has-[:checked]:border-olive-900`}>
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 accent-[#212B23]" checked={value === true} onChange={(e) => onChange(e.target.checked)} aria-invalid={!!error} data-testid={`${tid}-checkbox`} />
          <span className="text-base leading-relaxed">{field.label}</span>
        </label>
        {error && <p className="field-error" role="alert">{error}</p>}
      </div>
    );
  }
  if (field.type === "single_choice" || field.type === "yes_no" || field.type === "multi_choice") {
    const options = field.type === "yes_no" ? [{ value: true, label: "Yes" }, { value: false, label: "No" }] : field.options || [];
    const multi = field.type === "multi_choice";
    const checked = (v) => (multi ? (value || []).includes(v) : value === v);
    const toggle = (v) => (multi ? onChange(checked(v) ? value.filter((x) => x !== v) : [...(value || []), v]) : onChange(v));
    return (
      <fieldset className="mb-6" data-testid={tid}>
        <legend className="label">{field.label}{field.required && <span className="text-ink-500 font-normal"> (required)</span>}</legend>
        {field.help && <p className="help mb-2">{field.help}</p>}
        <div className={`flex flex-col gap-2 ${field.type === "yes_no" ? "sm:flex-row" : ""}`}>
          {options.map((o) => (
            <label key={String(o.value)} className={`tap justify-start gap-3 border bg-cream-100 px-4 cursor-pointer has-[:checked]:border-olive-900 has-[:checked]:ring-2 has-[:checked]:ring-olive-900/20 ${error ? "border-terracotta-600" : "border-sage-400"} ${field.type === "yes_no" ? "sm:min-w-[140px]" : ""}`}>
              <input type={multi ? "checkbox" : "radio"} name={field.key} className="h-5 w-5 accent-[#212B23]" checked={checked(o.value)} onChange={() => toggle(o.value)} data-testid={`${tid}-${String(o.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
        {error && <p className="field-error" role="alert">{error}</p>}
      </fieldset>
    );
  }
  const inputType = { phone: "tel", email: "email", date: "date" }[field.type] || "text";
  return (
    <Field label={field.label} required={field.required} help={field.help} error={error} testId={tid}>
      {(p) => field.type === "long_text"
        ? <textarea {...p} className="input min-h-[120px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${tid}-input`} />
        : <input {...p} type={inputType} className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${tid}-input`} />}
    </Field>
  );
};
